import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useBooking } from '../contexts/BookingContext';
import { db, doc, setDoc, serverTimestamp } from '../firebase/config';
import {
  makeShareToken,
  buildCartShareUrl,
  formatTimeLeft,
  msUntilExpiry,
} from '../utils/shareTokens';
import {
  X, ShoppingBag, Trash2, Plus, Minus, CreditCard, Truck, Shield,
  Calendar, Clock, User, MapPin, Check,
  Banknote, Wallet, Car, Store, Info,
  Share2, Link as LinkIcon, Copy, Loader2,
} from 'lucide-react';

const CartDrawer = () => {
  const navigate = useNavigate();
  const { user, userData } = useAuth();
  const {
    cartItems,
    cartTotal,
    cartCount,
    isCartOpen,
    closeCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartInitialTab,
  } = useCart();

  const {
    pendingBookings,
    removeBooking,
    computePendingPayNow,
    clearBookings,
  } = useBooking();

  const bookingCount = pendingBookings.length;
  const totalItems = cartCount + bookingCount;

  // Determine the initial tab intelligently
  const getDefaultTab = () => {
    // Prefer explicit cartInitialTab if set
    if (cartInitialTab) return cartInitialTab;
    // Otherwise: whichever has items
    if (cartCount > 0) return 'products';
    if (bookingCount > 0) return 'bookings';
    return 'products';
  };

  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // Share cart
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareExpiry, setShareExpiry] = useState(null);
  const [copied, setCopied] = useState(false);

  // When the drawer opens:
  //   - if cartInitialTab was explicitly set, use it
  //   - otherwise, auto-pick the tab that has items
  useEffect(() => {
    if (!isCartOpen) return;

    if (cartInitialTab) {
      setActiveTab(cartInitialTab);
      return;
    }

    // Auto-select the tab that actually has content
    if (cartCount > 0 && activeTab !== 'products') {
      setActiveTab('products');
    } else if (cartCount === 0 && bookingCount > 0 && activeTab !== 'bookings') {
      setActiveTab('bookings');
    }
  }, [isCartOpen, cartInitialTab, cartCount, bookingCount]);

  // ---- Products checkout → /checkout ----
  const handleProductsCheckout = () => {
    if (!user) {
      navigate('/login');
      closeCart();
      return;
    }
    if (cartItems.length === 0) {
      alert('Your bag is empty.');
      return;
    }
    setIsCheckingOut(true);
    navigate('/checkout');
    closeCart();
    setTimeout(() => setIsCheckingOut(false), 800);
  };

  // ---- Bookings checkout → /checkout ----
  const handleBookingsCheckout = () => {
    if (!user) {
      navigate('/login');
      closeCart();
      return;
    }
    if (pendingBookings.length === 0) {
      alert('No bookings in your bag.');
      return;
    }
    setIsCheckingOut(true);
    navigate('/checkout');
    closeCart();
    setTimeout(() => setIsCheckingOut(false), 800);
  };

  const payNowTotal = computePendingPayNow ? computePendingPayNow() : 0;
  const hasPayNow = payNowTotal > 0;

  const formatPrice = (price) => `R${Number(price || 0).toFixed(2)}`;

  const formatBookingDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch {
      return iso;
    }
  };

  const getProductImage = (item) => {
    if (item.image) return item.image;
    if (Array.isArray(item.media) && item.media.length > 0 && item.media[0].type === 'image') {
      return item.media[0].url;
    }
    if (item.mediaLegacy?.url) return item.mediaLegacy.url;
    return null;
  };

  const handleShareCart = async () => {
    if (!user) {
      alert('Sign in to share your cart.');
      return;
    }
    if (cartItems.length === 0) {
      alert('Add something to your cart before sharing.');
      return;
    }
    setShareLoading(true);
    try {
      const tokenPayload = makeShareToken(72);
      const shareDoc = {
        token: tokenPayload.token,
        ownerUid: user.uid,
        ownerName: userData?.name || 'A Skytone user',
        ownerImage: userData?.profileImage || null,
        items: cartItems.map((it) => ({
          id: it.id,
          name: it.name,
          brand: it.brand || null,
          price: Number(it.price || 0),
          quantity: Number(it.quantity || 1),
          image: getProductImage(it),
          options: it.options || {},
          professionalId: it.professionalId || null,
        })),
        subtotal: cartTotal,
        itemCount: cartCount,
        createdAt: serverTimestamp(),
        expiresAt: tokenPayload.expiresAt,
        sharedWith: null,
      };
      await setDoc(doc(db, 'shareLinks', tokenPayload.token), shareDoc);
      setShareUrl(buildCartShareUrl(tokenPayload.token));
      setShareExpiry(tokenPayload.expiresAt);
      setShareOpen(true);
    } catch (err) {
      console.error('Share failed:', err);
      alert('Could not create share link. Please try again.');
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Skytone cart',
          text: `Help me check out my Skytone cart 🎁`,
          url: shareUrl,
        });
      } catch (e) {}
    } else {
      handleCopy();
    }
  };

  const getPaymentLabel = (booking) => {
    const policy = booking.paymentPolicy || 'after';
    if (policy === 'after') return 'Pay after service';
    if (policy === 'full') return 'Full payment upfront';
    if (policy === 'deposit') return `${booking.depositPercent || 30}% deposit`;
    return 'Pay after service';
  };

  const getPaymentIcon = (booking) => {
    const policy = booking.paymentPolicy || 'after';
    if (policy === 'full') return <Wallet size={11} />;
    if (policy === 'deposit') return <CreditCard size={11} />;
    return <Banknote size={11} />;
  };

  const getLocationLabel = (booking) => {
    if (booking.locationMode === 'mobile') return 'Pro comes to you';
    return 'You go to pro';
  };

  const getLocationIcon = (booking) => {
    if (booking.locationMode === 'mobile') return <Car size={11} />;
    return <Store size={11} />;
  };

  const bookingsSummary = (() => {
    let totalDue = 0;
    let totalTransport = 0;

    pendingBookings.forEach(b => {
      const subtotal = Number(
        b.totalPrice ??
        ((b.price || 0) + Number(b.addonsTotal || 0) + (b.transportFee || 0))
      );
      totalDue += subtotal;
      totalTransport += Number(b.transportFee || 0);
    });

    return { totalDue, totalTransport, payNowTotal };
  })();

  if (!isCartOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={closeCart} />

      <div className="fixed top-0 right-0 w-full max-w-md h-full bg-white z-50 shadow-2xl flex flex-col">

        <div className="flex items-center justify-between p-4 border-b border-gray-50 shrink-0">
          <div className="flex items-center gap-3">
            <ShoppingBag size={24} className="text-[#EFA9B6]" />
            <h2 className="text-xl font-serif font-bold text-gray-900">Your Bag</h2>
            {totalItems > 0 && (
              <span className="text-xs bg-[#FFF5F6] text-[#EFA9B6] px-2 py-1 rounded-full font-bold">
                {totalItems} item{totalItems === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <button
            onClick={closeCart}
            className="w-10 h-10 rounded-full bg-[#FCF8F9] flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {totalItems > 0 && (
          <div className="flex gap-1 p-3 border-b border-gray-50 shrink-0">
            <button
              onClick={() => setActiveTab('products')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-bold transition-colors ${
                activeTab === 'products' ? 'bg-[#010101] text-white' : 'text-gray-500 hover:bg-[#FCF8F9]'
              }`}
            >
              <ShoppingBag size={14} />
              Products
              {cartCount > 0 && (
                <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10px] flex items-center justify-center ${
                  activeTab === 'products' ? 'bg-white text-[#010101]' : 'bg-[#FFF5F6] text-[#EFA9B6]'
                }`}>{cartCount}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('bookings')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-bold transition-colors ${
                activeTab === 'bookings' ? 'bg-[#010101] text-white' : 'text-gray-500 hover:bg-[#FCF8F9]'
              }`}
            >
              <Calendar size={14} />
              Bookings
              {bookingCount > 0 && (
                <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10px] flex items-center justify-center ${
                  activeTab === 'bookings' ? 'bg-white text-[#010101]' : 'bg-[#FFF5F6] text-[#EFA9B6]'
                }`}>{bookingCount}</span>
              )}
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'products' && (
            <>
              {cartItems.length === 0 ? (
                <EmptyProducts onClose={closeCart} />
              ) : (
                <div className="space-y-4">
                  {cartItems.map((item, index) => {
                    const img = getProductImage(item);
                    return (
                      <div key={index} className="flex gap-4 p-3 bg-[#FCF8F9] rounded-2xl border border-gray-50">
                        <div className="w-20 h-20 rounded-xl bg-white flex items-center justify-center shrink-0 overflow-hidden">
                          {img ? (
                            <img src={img} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <ShoppingBag size={24} className="text-gray-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start">
                            <div className="min-w-0">
                              <h4 className="font-bold text-sm text-gray-900 truncate">{item.name}</h4>
                              {item.brand && <p className="text-xs text-gray-400 truncate">{item.brand}</p>}
                            </div>
                            <button
                              onClick={() => removeFromCart(item.id, item.options)}
                              className="text-gray-400 hover:text-red-500 transition-colors shrink-0 ml-2"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2 bg-white rounded-full border border-gray-100 px-1">
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1, item.options)}
                                className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors"
                              >
                                <Minus size={14} />
                              </button>
                              <span className="font-bold text-sm text-gray-900 min-w-[16px] text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1, item.options)}
                                className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors"
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                            <span className="font-bold text-gray-900 text-sm">
                              {formatPrice(item.price * item.quantity)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <button
                    onClick={() => {
                      if (window.confirm('Clear all products from your bag?')) clearCart();
                    }}
                    className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                  >
                    Clear Products
                  </button>
                </div>
              )}
            </>
          )}

          {activeTab === 'bookings' && (
            <>
              {pendingBookings.length === 0 ? (
                <EmptyBookings onClose={closeCart} />
              ) : (
                <div className="space-y-4">
                  {pendingBookings.map((booking) => {
                    const subtotal = Number(
                      booking.totalPrice ??
                      ((booking.price || 0) + Number(booking.addonsTotal || 0) + (booking.transportFee || 0))
                    );
                    return (
                      <div key={booking.id} className="p-3 bg-[#FCF8F9] rounded-2xl border border-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div className="min-w-0 flex-1">
                            <h4 className="font-bold text-sm text-gray-900 truncate">
                              {booking.serviceTitle}
                            </h4>
                            {booking.optionName && (
                              <p className="text-[11px] font-bold text-[#AF8BD4] truncate">
                                {booking.optionName}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 truncate">
                              {booking.professionalName}
                            </p>
                          </div>
                          <button
                            onClick={() => removeBooking(booking.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors shrink-0 ml-2"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className="space-y-1.5 mb-3">
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Calendar size={12} />
                            <span>{formatBookingDate(booking.date)}</span>
                            <Clock size={12} className="ml-1" />
                            <span>{booking.time}</span>
                          </div>

                          {booking.duration && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Clock size={12} />
                              <span>{booking.duration}</span>
                            </div>
                          )}

                          {booking.locationMode && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              {getLocationIcon(booking)}
                              <span className="truncate">
                                {getLocationLabel(booking)}
                                {booking.locationMode === 'mobile' && booking.serviceAddress && (
                                  <> · {booking.serviceAddress}</>
                                )}
                                {booking.locationMode === 'salon' && booking.salonAddress && (
                                  <> · {booking.salonAddress}</>
                                )}
                              </span>
                            </div>
                          )}

                          {booking.transportFee > 0 && (
                            <div className="flex items-center gap-2 text-xs text-[#AF8BD4] font-bold">
                              <Car size={12} />
                              <span>Transport fee +{formatPrice(booking.transportFee)}</span>
                            </div>
                          )}

                          {Array.isArray(booking.addons) && booking.addons.length > 0 && (
                            <div className="flex items-center gap-2 text-xs text-[#AF8BD4] font-bold">
                              <Plus size={12} />
                              <span>
                                {booking.addons.map(a => a.name).join(', ')} (+{formatPrice(booking.addonsTotal || 0)})
                              </span>
                            </div>
                          )}
                        </div>

                        {booking.notes && (
                          <p className="text-xs text-gray-400 italic mb-2 line-clamp-2">
                            "{booking.notes}"
                          </p>
                        )}

                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                          <span className="text-[10px] font-bold text-[#EFA9B6] uppercase tracking-wider flex items-center gap-1.5">
                            {getPaymentIcon(booking)}
                            {getPaymentLabel(booking)}
                          </span>
                          <span className="font-bold text-gray-900 text-sm">
                            {formatPrice(subtotal)}
                          </span>
                        </div>

                        {booking.paymentPolicy === 'deposit' && booking.depositAmount > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-500 space-y-0.5">
                            <div className="flex justify-between">
                              <span>Deposit due now</span>
                              <span className="font-bold text-[#AF8BD4]">
                                {formatPrice(booking.depositAmount)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Balance at appointment</span>
                              <span className="font-bold text-gray-700">
                                {formatPrice(subtotal - booking.depositAmount)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <button
                    onClick={() => {
                      if (window.confirm('Clear all pending bookings?')) clearBookings();
                    }}
                    className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                  >
                    Clear Bookings
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-gray-50 bg-white shrink-0">
          {activeTab === 'products' && cartItems.length > 0 && (
            <>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Subtotal</span>
                  <span>{formatPrice(cartTotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Shipping</span>
                  <span className="text-green-500">Free</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 pt-2 border-t border-gray-50">
                  <span className="font-bold text-gray-900">Total</span>
                  <span className="font-bold text-gray-900 text-lg">{formatPrice(cartTotal)}</span>
                </div>
              </div>

              <button
                onClick={handleProductsCheckout}
                className="w-full bg-[#010101] text-white py-3.5 rounded-full font-bold text-sm hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
              >
                <CreditCard size={18} />
                {isCheckingOut ? 'Processing...' : 'Checkout Products'}
              </button>

              <button
                onClick={handleShareCart}
                disabled={shareLoading}
                className="w-full mt-2 bg-white border-2 border-[#FFF5F6] text-[#EFA9B6] py-3 rounded-full font-bold text-sm hover:bg-[#FFF5F6] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {shareLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Creating link…
                  </>
                ) : (
                  <>
                    <Share2 size={16} />
                    Share cart
                  </>
                )}
              </button>

              <div className="flex justify-center gap-6 mt-4">
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Truck size={14} /> Free Shipping
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Shield size={14} /> Secure Checkout
                </div>
              </div>
            </>
          )}

          {activeTab === 'bookings' && pendingBookings.length > 0 && (
            <>
              <div className="space-y-1.5 mb-3">
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Bookings total</span>
                  <span>{formatPrice(bookingsSummary.totalDue)}</span>
                </div>
                {bookingsSummary.totalTransport > 0 && (
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>· Includes transport</span>
                    <span>{formatPrice(bookingsSummary.totalTransport)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm pt-2 border-t border-gray-50">
                  <span className="font-bold text-gray-900">To pay now</span>
                  <span className="font-bold text-[#EFA9B6]">
                    {payNowTotal > 0
                      ? formatPrice(payNowTotal)
                      : 'R0.00 (pay later)'}
                  </span>
                </div>
              </div>

              <div className="mb-3 p-3 bg-[#FFF5F6] rounded-2xl">
                <div className="flex items-start gap-2">
                  <Info size={14} className="text-[#EFA9B6] shrink-0 mt-0.5" />
                  <p className="text-[11px] text-gray-600 leading-relaxed">
                    {hasPayNow ? (
                      <>
                        <span className="font-bold text-gray-900">Heads up:</span>{' '}
                        You'll be charged {formatPrice(payNowTotal)} at checkout. Your request goes to the
                        pro{bookingCount > 1 ? 's' : ''} once payment is complete.
                      </>
                    ) : (
                      <>
                        <span className="font-bold text-gray-900">Heads up:</span>{' '}
                        No payment due now. Your request will be sent to the pro{bookingCount > 1 ? 's' : ''} after checkout.
                      </>
                    )}
                  </p>
                </div>
              </div>

              <button
                onClick={handleBookingsCheckout}
                className="w-full bg-[#010101] text-white py-3.5 rounded-full font-bold text-sm hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
              >
                <CreditCard size={18} />
                {isCheckingOut ? 'Processing...' : 'Checkout Bookings'}
              </button>
            </>
          )}

          {totalItems === 0 && (
            <p className="text-center text-xs text-gray-400 py-2">Your bag is empty</p>
          )}
        </div>
      </div>

      {shareOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
          onClick={() => setShareOpen(false)}
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif font-bold text-xl text-gray-900">
                Share your cart
              </h3>
              <button
                onClick={() => setShareOpen(false)}
                className="w-8 h-8 rounded-full bg-[#FCF8F9] flex items-center justify-center hover:bg-gray-100"
              >
                <X size={16} />
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Send this link to anyone. They can see your cart and check it out for you — no account needed.
            </p>

            <div className="bg-[#FCF8F9] rounded-2xl p-3 flex items-center gap-2 mb-4">
              <LinkIcon size={16} className="text-[#EFA9B6] shrink-0" />
              <input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                className="bg-transparent border-none outline-none flex-1 text-xs text-gray-700 font-mono truncate"
              />
            </div>

            <div className="flex gap-2 mb-4">
              <button
                onClick={handleCopy}
                className="flex-1 py-3 rounded-full bg-[#FFF5F6] text-[#EFA9B6] font-bold text-sm hover:bg-[#FBE1E8] transition-colors flex items-center justify-center gap-2"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied!' : 'Copy link'}
              </button>
              <button
                onClick={handleNativeShare}
                className="flex-1 py-3 rounded-full bg-[#010101] text-white font-bold text-sm hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
              >
                <Share2 size={16} />
                Share
              </button>
            </div>

            {shareExpiry && (
              <p className="text-center text-[11px] text-gray-400">
                Link expires in {formatTimeLeft(msUntilExpiry({ expiresAt: shareExpiry }))}
              </p>
            )}
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes slide-in-right {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out forwards; }
      `}} />
    </>
  );
};

const EmptyProducts = ({ onClose }) => (
  <div className="flex flex-col items-center justify-center h-full text-center py-12">
    <div className="w-24 h-24 bg-[#FFF5F6] rounded-full flex items-center justify-center mb-4">
      <ShoppingBag size={40} className="text-[#EFA9B6]" />
    </div>
    <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">No products yet</h3>
    <p className="text-gray-400 text-sm mb-6">Browse the shop to add items</p>
    <button
      onClick={onClose}
      className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
    >
      Browse Shop
    </button>
  </div>
);

const EmptyBookings = ({ onClose }) => (
  <div className="flex flex-col items-center justify-center h-full text-center py-12">
    <div className="w-24 h-24 bg-[#FFF5F6] rounded-full flex items-center justify-center mb-4">
      <Calendar size={40} className="text-[#EFA9B6]" />
    </div>
    <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">No bookings yet</h3>
    <p className="text-gray-400 text-sm mb-6">Discover professionals and book a service</p>
    <button
      onClick={onClose}
      className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
    >
      Discover
    </button>
  </div>
);

export default CartDrawer;